import type { RouteLocationNormalized, RouteRecordRaw } from 'vue-router';

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: () => import('pages/home/HomePage.vue'),
    meta: {
      requiredAuth: false,
    },
    props: (route: RouteLocationNormalized) => ({
      pageNumber: route.query.pageNumber || '1',
      region: route.query.region || '',
      city: route.query.city || '',
      district: route.query.district || '',
      numberOfRooms: route.query.numberOfRooms || '',
      priceFrom: route.query.priceFrom || '',
      priceTo: route.query.priceTo || '',
      areaFrom: route.query.areaFrom || '',
      areaTo: route.query.areaTo || '',
      type: route.query.type || '',
      adType: route.query.adType || '',
    }),
  },
  {
    path: '/unit-details/:id',
    name: 'home-unit-details',
    component: () => import('pages/home/UnitDetailsPage.vue'),
    meta: {
      requiredAuth: false,
    },
    props: (route: RouteLocationNormalized) => ({
      unitId: route.params.id,
    }),
  },
  {
    path: '/account',
    name: 'account',
    component: () => import('pages/account/AccountOptionsPage.vue'),
    meta: {
      requiredAuth: true,
    },
  },
  {
    path: '/account/login',
    name: 'account-login',
    component: () => import('pages/account/LoginPage.vue'),
    meta: {
      requiredAuth: false,
    },
  },
  {
    path: '/account/register',
    name: 'account-register',
    component: () => import('pages/account/RegisterPage.vue'),
    meta: {
      requiredAuth: false,
    },
  },
  {
    path: '/account/send-otp',
    name: 'account-send-otp',
    component: () => import('pages/account/SendOtpPage.vue'),
    props: (route: RouteLocationNormalized) => ({
      operation: route.query.operation,
    }),
    meta: {
      requiredAuth: false,
    },
  },
  {
    path: '/account/verify-otp',
    name: 'account-verify-otp',
    component: () => import('pages/account/VerifyOtpPage.vue'),
    props: (route: RouteLocationNormalized) => ({
      operation: route.query.operation,
      target: route.query.target,
      codeExpiry: route.query.codeExpiry,
    }),
    meta: {
      requiredAuth: false,
    },
  },
  {
    path: '/account/reset-password/:id',
    name: 'account-reset-password',
    component: () => import('pages/account/ResetPasswordPage.vue'),
    props: (route: RouteLocationNormalized) => ({
      userId: route.params.id,
      accessToken: route.query.accessToken,
    }),
    meta: {
      requiredAuth: false,
    },
  },
  {
    path: '/account/personal-info',
    name: 'account-personal-info',
    component: () => import('pages/account/PersonalInfoPage.vue'),
    meta: {
      requiredAuth: true,
    },
  },
  {
    path: '/account/my-units',
    name: 'account-my-units',
    component: () => import('pages/account/MyUnitsPage.vue'),
    meta: {
      requiredAuth: true,
    },
  },
  {
    path: '/account/my-units/:id/update-details',
    name: 'account-unit-update-details',
    component: () => import('pages/account/unit/UpdateDetailsPage.vue'),
    props: (route: RouteLocationNormalized) => ({
      unitId: route.params.id,
    }),
    meta: {
      requiredAuth: true,
    },
  },
  {
    path: '/account/my-units/:id/update-photos',
    name: 'account-unit-update-photos',
    component: () => import('pages/account/unit/UpdatePhotosPage.vue'),
    props: (route: RouteLocationNormalized) => ({
      unitId: route.params.id,
    }),
    meta: {
      requiredAuth: true,
    },
  },
  {
    path: '/account/payment',
    name: 'account-payment-options',
    component: () => import('pages/account/payment/PaymentOptionsPage.vue'),
    props: (route: RouteLocationNormalized) => ({
      operationFor: route.query.operationFor,
      amount: route.query.amount,
      unitId: route.query.unitId,
    }),
    meta: {
      requiredAuth: true,
    },
  },
  {
    path: '/account/payment/checkout-form',
    name: 'account-payment-checkout-form',
    component: () => import('pages/account/payment/CheckoutFormPage.vue'),
    props: (route: RouteLocationNormalized) => ({
      operation: route.query.operation,
      amount: route.query.amount,
      paymentMethod: route.query.paymentMethod,
      operationFor: route.query.operationFor,
      unitId: route.query.unitId,
      accessToken: route.query.accessToken || undefined,
      userId: route.query.userId || undefined,
    }),
    meta: {
      // @note:set false to support open this page from apps
      requiredAuth: false,
    },
  },
  {
    path: '/account/payment/checkout-status/:paymentMethod',
    name: 'account-payment-checkout-status',
    component: () => import('pages/account/payment/CheckoutStatusPage.vue'),
    props: (route: RouteLocationNormalized) => ({
      paymentMethod: route.params.paymentMethod,
      checkoutId: route.query.id,
    }),
    meta: {
      requiredAuth: true,
    },
  },
  // @note(match-route-name-with-tab-name): the reason that we use add_unit instead of add-unit in route name is that in main layout we use the tab name to determine the current tab
  // so we need to use underscores because in MainLayout.vue we split the route name by dashes and take the first part as the tab name see:@note(match-route-name-with-tab-name)
  {
    path: '/add-unit',
    name: 'add_unit',
    component: () => import('pages/add-unit/CheckAdLicensePage.vue'),
    meta: {
      requiredAuth: true,
    },
  },
  {
    path: '/add-unit/nafath/create-request',
    name: 'add_unit-nafath-create-request',
    component: () => import('pages/add-unit/nafath/CreateRequestPage.vue'),
    meta: {
      requiredAuth: true,
    },
  },
  {
    path: '/add-unit/nafath/check-status',
    name: 'add_unit-nafath-check-status',
    component: () => import('pages/add-unit/nafath/CheckStatusPage.vue'),
    meta: {
      requiredAuth: true,
    },
    props: (route: RouteLocationNormalized) => ({
      transId: route.query.transId,
      random: route.query.random,
      nationalId: route.query.nationalId,
    }),
  },
  {
    path: '/add-unit/details',
    name: 'add_unit-details',
    component: () => import('pages/add-unit/UnitDetailsPage.vue'),
    meta: {
      requiredAuth: true,
    },
    props: (route: RouteLocationNormalized) => ({
      licenseNumber: route.query.licenseNumber,
      advertiserId: route.query.advertiserId,
      advertiserIdType: route.query.advertiserIdType,
    }),
  },

  {
    path: '/settings',
    name: 'settings',
    component: () => import('pages/settings/SettingsOptionsPage.vue'),
    meta: {
      requiredAuth: false,
    },
  },
  {
    path: '/settings/terms-and-conditions',
    name: 'settings-terms-and-conditions',
    component: () => import('pages/settings/TermsAndConditionsPage.vue'),
    meta: {
      requiredAuth: false,
    },
  },
  {
    path: '/settings/contact-us',
    name: 'settings-contact-us',
    component: () => import('pages/settings/ContactUsPage.vue'),
    meta: {
      requiredAuth: false,
    },
  },
  {
    path: '/settings/privacy-policy',
    name: 'settings-privacy-policy',
    component: () => import('pages/settings/PrivacyPolicyPage.vue'),
    meta: {
      requiredAuth: false,
    },
  },
  {
    path: '/:catchAll(.*)*',
    component: () => import('pages/ErrorNotFound.vue'),
    meta: {
      requiredAuth: false,
    },
  },
];

export default routes;
